import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { PointsAccountFacade } from '@features/points-account/facades/points-account/points-account.facade';
import { PointsSummaryFacade } from '@features/points-summary/facades/points-summary-facade/points-summary.facade';
import { UserPreferenceFacade } from '@features/user-preference/facades/user-preference/user-preference.facade';
import { Store } from '@ngrx/store';
import { getAuthIsLoggedIn } from '@rd/core/auth/store/selectors/auth.selectors';
import { getLoyaltyCurrencySelected } from '@rd/core/loyalty-currency/store/selectors/loyalty-currency.selectors';
import { FeatureFlagService } from '@rd/core/services/feature-flag/feature-flag.service';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { WindowRefService } from '@rd/core/services/window-ref/window-ref.service';
import {
  FeatureFlags,
  LoyaltyCurrency,
  NavLink,
  PointsAccount,
  UserPreference,
} from '@rd/types';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { AnzNavLinksService } from '../../services/anz-nav-links.service';
import { ANZ_SSO_ENABLED } from '../../tokens/features.tokens';
import { PageUrlSegment } from '../../types';
import { getMainUrlSegment } from '../../utils/anz-page-discovery.utils';

type MenuSection = 'rewards' | 'redeem' | 'cards' | 'home';
type UserAction = 'login' | 'logout' | 'enroll';

@Component({
  selector: 'rd-anz-side-nav-content',
  templateUrl: './side-nav-content.component.html',
  styleUrls: ['./side-nav-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnzSideNavContentComponent implements OnInit, OnDestroy {
  @HostBinding('class.rd-anz-side-nav-content') componentClass = true;

  @Input() cardSelectorDisabled = false;
  @Input() isSideNavOpen: boolean;
  @Input() sideNavOpen: Observable<void>;
  @Output() sideNavClose: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  userAction: EventEmitter<UserAction> = new EventEmitter<UserAction>();
  currentOpenMenu: MenuSection | null;
  currentUrlSegment: PageUrlSegment;

  isLoggedIn$: Observable<boolean | null>;
  loyaltyCurrency$: Observable<LoyaltyCurrency | null>;
  private destroy$ = new Subject();

  isAnzSSOEnabled = inject(ANZ_SSO_ENABLED);

  constructor(
    private urlUtilsService: UrlUtilsService,
    private iconRegistryService: IconRegistryService,
    private router: Router,
    private store: Store,
    private userPreferenceFacade: UserPreferenceFacade,
    public navlinksService: AnzNavLinksService,
    public pointsSummaryFacade: PointsSummaryFacade,
    public pointsAccountFacade: PointsAccountFacade,
    public cdRef: ChangeDetectorRef,
    private windowRefService: WindowRefService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.registerIcons();
    this.isLoggedIn$ = this.store.select(getAuthIsLoggedIn);
    this.loyaltyCurrency$ = this.store.select(getLoyaltyCurrencySelected);
  }

  isActivatedChildMenuItem(pageUrlSegment: string): boolean {
    const giftCardsPageList = [
      PageUrlSegment.GIFTCARD_LIST,
      PageUrlSegment.GIFTCARD_DETAILS,
      PageUrlSegment.GIFTCARD_REVIEW,
      PageUrlSegment.GIFTCARD_SUCCESS,
      PageUrlSegment.GIFTCARD_AUTOREDEMPTION,
      PageUrlSegment.GIFTCARD_AUTOREDEMPTION_SUCCESS,
    ];

    const transferProgramsPageList = [
      PageUrlSegment.TRANSFER_PROGRAMS,
      PageUrlSegment.TRANSFER_LANDING,
      PageUrlSegment.TRANSFER_REVIEW,
      PageUrlSegment.TRANSFER_SUCCESS,
      PageUrlSegment.TRANSFER_AUTOREDEMPTION,
      PageUrlSegment.TRANSFER_AUTOREDEMPTION_SUCCESS,
    ];

    const redeemCashPageList = [
      PageUrlSegment.REDEEMCASH_DETAILS,
      PageUrlSegment.REDEEMCASH_REVIEW,
      PageUrlSegment.REDEEMCASH_SUCCESS,
      PageUrlSegment.REDEEMCASH_ENROLLMENT,
      PageUrlSegment.REDEEMCASH_AUTOREDEMPTION,
      PageUrlSegment.REDEEMCASH_AUTOREDEMPTION_SUCCESS,
    ];

    const orderPageList = [
      PageUrlSegment.ORDERS,
      PageUrlSegment.PAST_ORDERS,
      PageUrlSegment.MANAGE_REDEMPTIONS,
    ];

    const merchandisePageList = [
      PageUrlSegment.MERCHANDISE,
      PageUrlSegment.MERCHANDISE_ALL,
      PageUrlSegment.MERCHANDISE_TOP_50,
      PageUrlSegment.MERCHANDISE_CATEGORY,
      PageUrlSegment.MERCHANDISE_DETAILS,
    ];

    const everydaySpendPageList = [
      PageUrlSegment.EVERYDAY_SPEND_CHECKOUT,
      PageUrlSegment.EVERYDAY_SPEND_REVIEW,
      PageUrlSegment.EVERYDAY_SPEND_SUCCESS,
      PageUrlSegment.EVERYDAY_SPEND_AUTOREDEMPTION,
      PageUrlSegment.EVERYDAY_SPEND_AUTOREDEMPTION_SUCCESS,
    ];

    switch (pageUrlSegment) {
      case PageUrlSegment.GIFTCARD_LIST: {
        return giftCardsPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.TRANSFER_PROGRAMS: {
        return transferProgramsPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.REDEEMCASH_DETAILS: {
        return redeemCashPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.POINTS_TRANSACTIONS: {
        return this.currentUrlSegment === PageUrlSegment.POINTS_TRANSACTIONS;
      }
      case PageUrlSegment.MERCHANDISE: {
        return merchandisePageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.ORDERS: {
        return this.currentUrlSegment === PageUrlSegment.ORDERS;
      }
      case PageUrlSegment.HOMEPAGE: {
        return this.currentUrlSegment === PageUrlSegment.HOMEPAGE;
      }
      case PageUrlSegment.CONTACT_US: {
        return this.currentUrlSegment === PageUrlSegment.CONTACT_US;
      }
      case PageUrlSegment.FAQ: {
        return this.currentUrlSegment === PageUrlSegment.FAQ;
      }
      case PageUrlSegment.SECURITY_AND_PRIVACY_STATEMENT: {
        return (
          this.currentUrlSegment ===
          PageUrlSegment.SECURITY_AND_PRIVACY_STATEMENT
        );
      }
      case PageUrlSegment.TERMS: {
        return this.currentUrlSegment === PageUrlSegment.TERMS;
      }
      case PageUrlSegment.MANAGE_REDEMPTIONS:
      case PageUrlSegment.PAST_ORDERS: {
        if (this.featureFlagService.isEnable(FeatureFlags.AutoRedemption)) {
          return orderPageList.includes(this.currentUrlSegment);
        }
        break;
      }
      case PageUrlSegment.EVERYDAY_SPEND_CHECKOUT: {
        return everydaySpendPageList.includes(this.currentUrlSegment);
      }
    }
    return false;
  }

  ngOnInit(): void {
    this.sideNavOpen.subscribe(() => {
      this.cdRef.detectChanges();
    });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrlSegment = getMainUrlSegment(event.urlAfterRedirects);
        this.currentOpenMenu = this.initializeCurrentOpenMenu();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  private registerIcons(): void {
    this.iconRegistryService.registerIcon('close');
    this.iconRegistryService.registerIcon('chevron-down');
    this.iconRegistryService.registerIcon('logout');
  }

  isExternalNavLink(navLink: NavLink): boolean {
    const navLinkString = navLink.route.toString();
    return (
      navLinkString.startsWith('http://') ||
      navLinkString.startsWith('https://')
    );
  }

  emitSideNavClose(): void {
    this.sideNavClose.emit();
  }

  getExpandedStatus(menuType: string): boolean {
    return this.currentOpenMenu === menuType;
  }

  toggleMenu(menuSection: MenuSection): void {
    if (menuSection === 'cards' && this.cardSelectorDisabled) {
      return;
    }
    if (this.currentOpenMenu !== menuSection) {
      this.currentOpenMenu = menuSection;
      this.currentUrlSegment = getMainUrlSegment(
        this.windowRefService.nativeWindow.location.pathname,
      );
    } else {
      this.currentOpenMenu = null;
    }
  }

  emitUserAction(action: UserAction): void {
    this.userAction.emit(action);
  }

  get pointsAccountId$(): Observable<string | undefined> {
    return this.userPreferenceFacade.userPreferences$.pipe(
      map((userPreference: UserPreference) => userPreference.pointsAccountId),
    );
  }

  isPointsAccountActive(
    pointsAccount: PointsAccount,
    pointsAccountId: string,
  ): boolean {
    return pointsAccount.id === pointsAccountId;
  }

  click(pointsAccount: PointsAccount): void {
    this.userPreferenceFacade.userPreferences$
      .pipe(
        take(1),
        map((userPreference: UserPreference) => userPreference.pointsAccountId),
      )
      .subscribe((pointsAccountId) => {
        if (pointsAccountId !== pointsAccount.id) {
          this.userPreferenceFacade.updatePointsAccountId(pointsAccount.id);
        }
        this.currentOpenMenu = null;
        this.emitSideNavClose();
      });
  }

  get activePointsAccount$(): Observable<PointsAccount | undefined> {
    return combineLatest([
      this.pointsAccountFacade.pointsAccounts$,
      this.pointsAccountId$,
    ]).pipe(
      map(([pointsAccounts, id]) =>
        pointsAccounts.find((account) => account.id === id),
      ),
      filter((value) => value !== undefined),
    );
  }

  get shouldRenderCardsList$(): Observable<boolean> {
    return this.pointsAccountFacade.pointsAccounts$.pipe(
      map((pointsAccounts) => pointsAccounts.length > 1),
    );
  }

  get getShouldRenderPointsSummary$(): Observable<boolean> {
    return this.pointsAccountFacade.pointsAccounts$.pipe(
      map((pointsAccounts) => pointsAccounts.length > 0),
    );
  }

  get buttonIconPath(): string {
    return this.urlUtilsService.imageUrl('/icons/padlock.svg');
  }

  private initializeCurrentOpenMenu(): MenuSection {
    const giftCardPageList = [
      PageUrlSegment.GIFTCARD_LIST,
      PageUrlSegment.GIFTCARD_DETAILS,
      PageUrlSegment.GIFTCARD_REVIEW,
      PageUrlSegment.GIFTCARD_SUCCESS,
      PageUrlSegment.GIFTCARD_AUTOREDEMPTION,
      PageUrlSegment.GIFTCARD_AUTOREDEMPTION_SUCCESS,
    ];

    const redeemCashPageList = [
      PageUrlSegment.REDEEMCASH_DETAILS,
      PageUrlSegment.REDEEMCASH_REVIEW,
      PageUrlSegment.REDEEMCASH_SUCCESS,
      PageUrlSegment.REDEEMCASH_ENROLLMENT,
      PageUrlSegment.REDEEMCASH_AUTOREDEMPTION,
      PageUrlSegment.REDEEMCASH_AUTOREDEMPTION_SUCCESS,
    ];

    const transferPageList = [
      PageUrlSegment.TRANSFER_PROGRAMS,
      PageUrlSegment.TRANSFER_LANDING,
      PageUrlSegment.TRANSFER_REVIEW,
      PageUrlSegment.TRANSFER_SUCCESS,
      PageUrlSegment.TRANSFER_AUTOREDEMPTION,
      PageUrlSegment.TRANSFER_AUTOREDEMPTION_SUCCESS,
    ];

    const merchandisePageList = [
      PageUrlSegment.MERCHANDISE,
      PageUrlSegment.MERCHANDISE_ALL,
      PageUrlSegment.MERCHANDISE_TOP_50,
      PageUrlSegment.MERCHANDISE_CATEGORY,
      PageUrlSegment.MERCHANDISE_DETAILS,
    ];

    const everydaySpendPageList = [
      PageUrlSegment.EVERYDAY_SPEND_CHECKOUT,
      PageUrlSegment.EVERYDAY_SPEND_REVIEW,
      PageUrlSegment.EVERYDAY_SPEND_SUCCESS,
      PageUrlSegment.EVERYDAY_SPEND_AUTOREDEMPTION,
      PageUrlSegment.EVERYDAY_SPEND_AUTOREDEMPTION_SUCCESS,
    ];

    const orderPageList = [
      PageUrlSegment.ORDERS,
      PageUrlSegment.PAST_ORDERS,
      PageUrlSegment.MANAGE_REDEMPTIONS,
    ];

    const redeemPageList = [
      ...giftCardPageList,
      ...redeemCashPageList,
      ...transferPageList,
      ...merchandisePageList,
      ...everydaySpendPageList,
    ];

    if (redeemPageList.includes(this.currentUrlSegment)) {
      return 'redeem';
    } else if (
      this.currentUrlSegment === PageUrlSegment.POINTS_TRANSACTIONS ||
      orderPageList.includes(this.currentUrlSegment)
    ) {
      return 'rewards';
    } else {
      return 'home';
    }
  }
}
